import axios from 'axios';

// 创建axios实例
const api = axios.create({
  // 使用相对路径，配合vue.config.js中的代理设置
  baseURL: '/api',
  timeout: 60000, // 上传头像等大请求需要更长超时
});

// 请求拦截器 - 添加token和管理后台标识
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 添加管理后台请求标识，确保后端返回管理员权限的数据
    config.headers['x-request-source'] = 'management';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理错误
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API方法
const apiExports = {
  // 登录
  login: (credentials) => api.post('/admin/login', credentials),
  
  // 活动管理
  getActivities: (params) => api.get('/admin/activities', { params }),
  createActivity: (data) => api.post('/activities', data),
  updateActivity: (id, data) => api.put(`/activities/${id}`, data),
  deleteActivity: (id) => api.delete(`/activities/${id}`),
  getActivity: (id) => api.get(`/activities/${id}`),
  toggleFeatured: (id, isFeatured) => api.patch(`/activities/${id}/featured`, { isFeatured }),
  changeStatus: (id, status) => api.patch(`/activities/${id}/status`, { status }),
  
  // 奖金发放管理
  getPrizeDistributionStatus: (activityId) => api.get(`/admin/activities/${activityId}/prize-distribution`),
  manualDistributePrize: (activityId) => api.post(`/admin/activities/${activityId}/distribute-prize`),
  
  // 订单管理
  getOrders: (params) => api.get('/admin/orders', { params }),
  getOrderDetail: (id) => api.get(`/admin/orders/${id}`),
  processRefund: (id, data) => api.post(`/admin/orders/${id}/refund`, data),
  cancelOrder: (id) => api.patch(`/admin/orders/${id}/cancel`),

  // 用户管理
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserStats: () => api.get('/admin/users/stats'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  getUserInfo: (id) => api.get(`/admin/users/${id}`),
  // 单次发起券（可举办活动次数）
  getUserActivityTickets: (id) => api.get(`/admin/users/${id}/activity-tickets`),
  grantUserActivityTickets: (id, data) => api.post(`/admin/users/${id}/activity-tickets`, data),
  // 由于后端没有单独的获取用户积分和荣誉的API,
  // 我们提供mock数据作为临时解决方案
  getUserPoints: (userId) => Promise.resolve({ success: true, data: { points: 0, lastUpdateTime: new Date().toISOString() } }),
  updateUserPoints: (userId, data) => api.post(`/admin/user/${userId}/points/update`, data),
  getUserHonors: (userId) => Promise.resolve({ success: true, data: [] }),
  // 新增活跃度分数相关方法
  updateUserActivityScore: (userId) => api.put(`/admin/users/${userId}`, { autoCalculateActivityScore: true }).then(data => ({
    success: data.success,
    activityScore: data.data?.activityScore || 0
  })),
  getUserActivityScore: (userId) => api.get(`/admin/users/${userId}`).then(data => ({
    success: data.success,
    activityScore: data.data?.activityScore || 0
  })),
  
  // 战队管理
  getTeams: (params) => {
    // 将前端参数名映射为后端期望的参数名
    const mappedParams = { ...params };
    if (mappedParams.pageSize) {
      mappedParams.limit = mappedParams.pageSize;
      delete mappedParams.pageSize;
    }
    if (mappedParams.keyword) {
      mappedParams.search = mappedParams.keyword;
      delete mappedParams.keyword;
    }
    return api.get('/admin/teams', { params: mappedParams });
  },
  getTeamInfo: (id) => api.get(`/admin/teams/${id}`),
  createTeam: (data) => api.post('/admin/teams', data),
  updateTeam: (id, data) => api.put(`/admin/teams/${id}`, data),
  uploadTeamLogo: (id, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post(`/admin/teams/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  toggleTeamStatus: (id) => api.patch(`/admin/teams/${id}/status`),
  getTeamPoints: (teamId) => api.get(`/admin/teams/${teamId}/points`),
  updateTeamPoints: (teamId, data) => api.post(`/admin/teams/${teamId}/points/update`, data),
  getTeamHonors: (teamId) => api.get(`/admin/teams/${teamId}/honors`),
  getTeamMembers: (teamId) => api.get(`/admin/teams/${teamId}/members`),
  addTeamMember: (teamId, data) => api.post(`/admin/teams/${teamId}/members`, data),
  removeTeamMember: (teamId, userId) => api.delete(`/admin/teams/${teamId}/members/${userId}`),
  // 战队参赛相关
  getTeamMatches: (teamId, params) => api.get(`/admin/teams/${teamId}/matches`, { params }),
  getTeamActivities: (teamId) => api.get(`/admin/teams/${teamId}/activities`),
  updateTeamPrizeMethod: (teamId, data) => api.put(`/admin/teams/${teamId}`, data),
  
  // 运动类型管理
  getSportTypes: () => api.get('/admin/sport-types'),
  createSportType: (data) => api.post('/admin/sport-types', data),
  updateSportType: (id, data) => api.put(`/admin/sport-types/${id}`, data),
  deleteSportType: (id) => api.delete(`/admin/sport-types/${id}`),

  // 仪表盘数据
  getDashboardData: () => api.get('/admin/dashboard/stats'),
  getUserGrowth: (range) => api.get('/admin/dashboard/user-growth', { params: { range } }),
  getActivityTypes: () => api.get('/admin/dashboard/activity-types'),
  getRecentActivities: () => api.get('/admin/dashboard/recent-activities'),
  getRecentUsers: () => api.get('/admin/dashboard/recent-users'),
  
  // 系统设置
  getSystemSettings: () => api.get('/admin/settings'),
  updateSystemSettings: (data) => api.put('/admin/settings', data),
  testEmail: () => api.post('/admin/settings/test-email'),
  backupDatabase: () => api.post('/admin/backup'),
  restoreDatabase: (filename) => api.post(`/admin/backup/restore/${filename}`),
  cleanLogs: () => api.delete('/admin/settings/logs'),
  getBackups: () => api.get('/admin/backups'),
  downloadBackup: (filename) => api.get(`/admin/backups/${filename}`, { responseType: 'blob' }),
  deleteBackup: (filename) => api.delete(`/admin/backups/${filename}`),

  // Excel导出
  getExportTables: () => api.get('/admin/export/tables'),
  exportExcel: (tables) => api.post('/admin/export/excel', { tables }),
  downloadExcel: (filename) => api.get(`/admin/export/download/${filename}`, { responseType: 'blob' }),

  // 管理员个人资料
  getProfile: () => api.get('/admin/profile'),
  updateProfile: (data) => api.put('/admin/profile', data),
  changePassword: (data) => api.post('/admin/change-password', data),

  // 管理员账号管理
  getAdminAccounts: () => api.get('/admin/accounts'),
  createAdminAccount: (data) => api.post('/admin/accounts', data),
  updateAdminAccount: (id, data) => api.put(`/admin/accounts/${id}`, data),
  deleteAdminAccount: (id) => api.delete(`/admin/accounts/${id}`),
  resetAdminPassword: (id, newPassword) => api.post(`/admin/accounts/${id}/reset-password`, { newPassword }),



  // 会员权益管理
  getMembershipLevels: () => api.get('/admin/membership/levels'),
  getMembershipLevel: (id) => api.get(`/admin/membership/levels/${id}`),
  createMembershipLevel: (data) => api.post('/admin/membership/levels', data),
  updateMembershipLevel: (id, data) => api.put(`/admin/membership/levels/${id}`, data),
  deleteMembershipLevel: (id) => api.delete(`/admin/membership/levels/${id}`),
  
  // 意见反馈管理
  getFeedbacks: (params) => api.get('/admin/feedbacks', { params }),
  updateFeedback: (id, data) => api.put(`/admin/feedbacks/${id}`, data),
  deleteFeedback: (id) => api.delete(`/admin/feedbacks/${id}`),
  
  // 文档管理
  getDocuments: (params) => api.get('/admin/documents', { params }),
  createDocument: (data) => api.post('/admin/documents', data),
  updateDocument: (id, data) => api.put(`/admin/documents/${id}`, data),
  deleteDocument: (id) => api.delete(`/admin/documents/${id}`),
  
  // 支付配置管理
  getPaymentConfig: () => api.get('/admin/payment/config'),
  updatePaymentConfig: (platform, config) => api.post('/admin/payment/config', { platform, config }),
  testPaymentConnection: (platform) => api.post('/admin/payment/config/test', { platform }),
  
  // 退款管理
  processRefund: (id, data) => api.post(`/admin/orders/${id}/refund`, data),
  getRefundRecords: (params) => api.get('/admin/refunds', params),
  getRefundRecord: (id) => api.get(`/admin/refunds/${id}`),

  // 人工审核
  getManualReviews: (params) => api.get('/admin/manual-review', { params }),
  getManualReviewPendingCount: () => api.get('/admin/manual-review/pending-count'),
  approveManualReview: (id, data) => api.post(`/admin/manual-review/${id}/approve`, data),
  rejectManualReview: (id, data) => api.post(`/admin/manual-review/${id}/reject`, data),

  // 风控限额配置
  getRiskControlConfig: () => api.get('/admin/risk-control/config'),
  updateRiskControlConfig: (data) => api.put('/admin/risk-control/config', data),

  // 敏感词管理
  getSensitiveWords: (params) => api.get('/admin/sensitive-words', { params }),
  addSensitiveWord: (data) => api.post('/admin/sensitive-words', data),
  updateSensitiveWord: (id, data) => api.put(`/admin/sensitive-words/${id}`, data),
  deleteSensitiveWord: (id) => api.delete(`/admin/sensitive-words/${id}`),
  getSensitiveWordCategories: () => api.get('/admin/sensitive-words/categories'),

  // 内容违规记录
  getContentViolations: (params) => api.get('/admin/content-violations', { params }),
  addViolationToLocal: (data) => api.post('/admin/sensitive-words/add-from-violation', data)
};

export default apiExports;

// 命名导出：兼容组件的按需导入（import { xxx } from '@/utils/api'），与 default 导出同源
export const getPaymentConfig = apiExports.getPaymentConfig;
export const updatePaymentConfig = apiExports.updatePaymentConfig;
export const testPaymentConnection = apiExports.testPaymentConnection;
export const getRefundRecords = apiExports.getRefundRecords;
export const getRefundRecord = apiExports.getRefundRecord;